'use client';

import { useEffect, useState } from 'react';

interface Heading {
    id: string;
    text: string;
    level: number;
}

export default function ArticleTOC({ headings }: { headings: Heading[] }) {
    const [activeId, setActiveId] = useState<string>('');

    useEffect(() => {
        // Observer to track which heading is currently active
        // We look for headings crossing the top "read line" of the screen
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id);
                    }
                });
            },
            {
                // Trigger when the element is near the top of the viewport
                // -100px from top (header offset)
                // -80% from bottom (ignore elements far down)
                rootMargin: '-100px 0px -80% 0px'
            }
        );

        headings.forEach((heading) => {
            const element = document.getElementById(heading.id);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, [headings]);

    if (headings.length === 0) return null;

    return (
        <nav className="space-y-4">
            <h4 className="text-gray-300 font-bold uppercase text-xs tracking-wider mb-4 border-b border-white/10 pb-2">
                Contents
            </h4>
            <ul className="space-y-3 pl-2 relative">
                {headings.map((heading) => (
                    <li key={heading.id} className={`${heading.level === 3 ? 'ml-4' : ''} relative`}>
                        <a
                            href={`#${heading.id}`}
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById(heading.id)?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start'
                                });
                                // Manually set active immediately for responsiveness
                                setActiveId(heading.id);
                            }}
                            className={`block text-sm transition-all duration-200 leading-relaxed ${activeId === heading.id
                                ? 'text-[#00ff9d] font-bold translate-x-2'
                                : 'text-gray-400 hover:text-white hover:translate-x-1'
                                }`}
                        >
                            {/* Active Side Indicator */}
                            <span
                                className={`absolute -left-[11px] top-1/2 -translate-y-1/2 w-[3px] rounded-full transition-all duration-300 ${activeId === heading.id ? 'h-6 bg-[#00ff9d] opacity-100' : 'h-0 bg-transparent opacity-0'
                                    }`}
                            />
                            {heading.text}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
}
